import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Spinner } from "@app/components/common/icons";
import { Currency } from "@app/types/currencies";
import Chart from "./components/chart/Chart";
import { TimePeriod } from "./components/time-period/TimePeriod";
import { useHistoricalData } from "./useHistoricalData";
import { Period } from "./types";

type Props = {
  baseCurrency: Currency;
  convertCurrency: Currency;
  title?: string;
};

export const HistoricalChart: React.FC<Props> = ({
  baseCurrency,
  convertCurrency,
  title,
}) => {
  const { t } = useTranslation("common");
  const [period, setPeriod] = useState(Period.YEAR_TO_DAY);
  const timeSeriesData = useHistoricalData(baseCurrency, period);

  const series = useMemo(
    () =>
      Object.entries(timeSeriesData.rates ?? {}).map(([date, rates]) => ({
        date,
        rate: rates[convertCurrency],
      })),
    [baseCurrency, convertCurrency, timeSeriesData],
  );

  return (
    <div
      className={classNames(
        "relative z-20",
        "md:rounded-lg md:px-10 md:py-10",
        "xxl:px-[150px] xxl:py-20",
      )}
    >
      <div
        className={classNames(
          "absolute bottom-0 left-0 right-0 top-0 hidden rounded-lg bg-blue-charcoal",
          "md:block",
        )}
      />
      {title ? (
        <h4
          className={classNames(
            "relative z-20 mb-6 text-medium text-primary-content",
            "md:text-large",
          )}
        >
          {title}
        </h4>
      ) : null}
      {timeSeriesData.isError ? (
        <div className="flex h-[415px] items-center justify-center">
          <span className="relative z-20 text-center text-regular">
            {t("error")}
          </span>
        </div>
      ) : timeSeriesData.isLoading ? (
        <div
          className={classNames("flex h-[415px] items-center justify-center")}
        >
          <Spinner
            className={classNames(
              "relative z-20 h-10 w-10 stroke-primary-content",
            )}
          />
        </div>
      ) : (
        <div className={classNames("pt-10")}>
          <Chart
            series={series}
            baseCurrency={baseCurrency}
            converCurrency={convertCurrency}
          />
        </div>
      )}

      <TimePeriod selectedPeriod={period} onSelect={setPeriod} />
    </div>
  );
};
